import {
  Badge, Box, Flex, Grid, Heading, Progress,
  Spinner, Stat, StatHelpText, StatLabel, StatNumber,
  Table, Tbody, Td, Text, Th, Thead, Tr,
  useColorModeValue,
} from '@chakra-ui/react'
import Card from 'components/Card/Card'
import CardBody from 'components/Card/CardBody'
import CardHeader from 'components/Card/CardHeader'
import { CartIcon, DocumentIcon, PersonIcon, StatsIcon } from 'components/Icons/Icons'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GET_DASHBOARD_STATS } from 'store/dashboard'

function StatCard({ label, value, helpText, icon, color }) {
  const textColor = useColorModeValue('gray.700', 'white')
  const iconBg = useColorModeValue(`${color}.100`, `${color}.800`)
  return (
    <Card>
      <CardBody>
        <Flex align='center' gap={4}>
          <Box bg={iconBg} borderRadius='12px' p={3} flexShrink={0}>
            {React.cloneElement(icon, { color: `${color}.500`, w: '24px', h: '24px' })}
          </Box>
          <Stat>
            <StatLabel color='gray.400' fontSize='sm'>{label}</StatLabel>
            <StatNumber color={textColor} fontSize='2xl' fontWeight='bold'>{value ?? '-'}</StatNumber>
            {helpText && <StatHelpText color='gray.400' mb={0}>{helpText}</StatHelpText>}
          </Stat>
        </Flex>
      </CardBody>
    </Card>
  )
}

function TopUsersTable({ users, textColor, subColor }) {
  if (users.length === 0) return <Text color={subColor} textAlign='center' py={4}>ยังไม่มีข้อมูล</Text>
  return (
    <Table size='sm'>
      <Thead>
        <Tr>
          <Th>#</Th>
          <Th>ชื่อ</Th>
          <Th>ชั้นเรียน</Th>
          <Th isNumeric>ครั้งที่ทำ</Th>
          <Th isNumeric>คะแนนเฉลี่ย</Th>
        </Tr>
      </Thead>
      <Tbody>
        {users.map((u, i) => (
          <Tr key={u.id}>
            <Td>
              <Text fontWeight='bold' color={i < 3 ? 'teal.500' : textColor}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
              </Text>
            </Td>
            <Td>
              <Text fontWeight='semibold' color={textColor} fontSize='sm'>{u.name}</Text>
              <Text fontSize='xs' color={subColor}>{u.school}</Text>
            </Td>
            <Td><Badge colorScheme='teal' fontSize='xs'>{u.class}</Badge></Td>
            <Td isNumeric><Badge colorScheme='blue'>{u.exam_count}</Badge></Td>
            <Td isNumeric>
              <Text fontSize='sm' fontWeight='bold' minW='36px' textAlign='right'>
                {parseFloat(u.avg_score || 0).toFixed(1)}%
              </Text>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  )
}

export default function Dashboard() {
  const dispatch = useDispatch()
  const stats = useSelector((state) => state.dashboard.stats)
  const textColor = useColorModeValue('gray.700', 'white')
  const subColor = useColorModeValue('gray.500', 'gray.400')

  const initFetch = useCallback(() => {
    dispatch(GET_DASHBOARD_STATS())
  }, [dispatch])

  useEffect(() => { initFetch() }, [initFetch])

  if (!stats) {
    return (
      <Flex pt={{ base: '80px', md: '75px' }} justify='center' align='center' minH='60vh'>
        <Spinner size='xl' color='teal.500' />
      </Flex>
    )
  }

  const { userStats, courseStats, worksheetStats, examQuestionStats, topUsers, topUsersThisMonth, recentUsers, popularCourses } = stats

  return (
    <Flex direction='column' pt={{ base: '80px', md: '75px' }} gap={6}>

      {/* ===== Stat Cards ===== */}
      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }} gap={4}>
        <StatCard
          label='ผู้ใช้งานทั้งหมด'
          value={userStats.total_users}
          helpText={`ใช้งานอยู่ ${userStats.active_users} คน`}
          icon={<PersonIcon />}
          color='teal'
        />
        <StatCard
          label='คอร์สเรียน (เปิดใช้งาน)'
          value={courseStats.active_courses}
          helpText={`ทั้งหมด ${courseStats.total_courses} คอร์ส`}
          icon={<DocumentIcon />}
          color='blue'
        />
        <StatCard
          label='ใบงาน (เปิดใช้งาน)'
          value={worksheetStats.active_worksheets}
          helpText={`ทั้งหมด ${worksheetStats.total_worksheets} ใบงาน`}
          icon={<CartIcon />}
          color='purple'
        />
        <StatCard
          label='ข้อสอบ'
          value={examQuestionStats.total_exams}
          helpText={`${examQuestionStats.total_questions} คำถาม`}
          icon={<StatsIcon />}
          color='orange'
        />
      </Grid>

      {/* ===== New Users Last 30 Days ===== */}
      <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={4}>
        <Card>
          <Box bg='teal.50' borderRadius='8px' px={4} py={2} mb={2} display='inline-block'>
            <Text fontSize='sm' color='teal.600' fontWeight='bold'>
              ✨ ผู้ใช้ใหม่ (30 วันล่าสุด): {userStats.new_users_30d} คน
            </Text>
          </Box>
        </Card>
        <Card>
          <Box bg='blue.50' borderRadius='8px' px={4} py={2} mb={2} display='inline-block'>
            <Text fontSize='sm' color='blue.600' fontWeight='bold'>
              📊 คะแนนเฉลี่ยผู้ทำข้อสอบ (Top 10):&nbsp;
              {topUsers.length > 0
                ? Math.round(topUsers.reduce((s, u) => s + parseFloat(u.avg_score || 0), 0) / topUsers.length)
                : '-'}
              &nbsp;คะแนน
            </Text>
          </Box>
        </Card>
      </Grid>

      {/* ===== Top Users + Recent Users ===== */}
      <Grid templateColumns={{ base: '1fr', xl: '2fr 1fr' }} gap={4}>

        {/* Top Users All Time */}
        <Card>
          <CardHeader>
            <Heading size='md' color={textColor}>🏆 Top ผู้ใช้งาน (ทำข้อสอบมากที่สุด)</Heading>
          </CardHeader>
          <CardBody overflowX='auto'>
            <TopUsersTable users={topUsers} textColor={textColor} subColor={subColor} />
          </CardBody>
        </Card>

        {/* Recent Users */}
        <Card>
          <CardHeader>
            <Heading size='md' color={textColor}>🆕 ผู้ใช้สมัครใหม่ล่าสุด</Heading>
          </CardHeader>
          <CardBody>
            {recentUsers.length === 0 ? (
              <Text color={subColor} textAlign='center' py={4}>ยังไม่มีข้อมูล</Text>
            ) : (
              <Flex direction='column' gap={3}>
                {recentUsers.map((u) => (
                  <Flex key={u.id} align='center' gap={3}>
                    <Box
                      w='38px' h='38px' borderRadius='full' bg='teal.100'
                      display='flex' alignItems='center' justifyContent='center' flexShrink={0}
                    >
                      <Text fontWeight='bold' color='teal.600' fontSize='sm'>
                        {u.name?.charAt(0) || '?'}
                      </Text>
                    </Box>
                    <Box flex={1} minW={0}>
                      <Text fontSize='sm' fontWeight='semibold' color={textColor} noOfLines={1}>{u.name}</Text>
                      <Text fontSize='xs' color={subColor}>{u.class} · {u.school}</Text>
                    </Box>
                    <Badge
                      colorScheme={u.status === 'Active' ? 'green' : u.status === 'Pending Approval' ? 'orange' : 'gray'}
                      fontSize='xs' flexShrink={0}
                    >
                      {u.status === 'Active' ? 'ใช้งาน' : u.status === 'Pending Approval' ? 'รออนุมัติ' : 'ปิด'}
                    </Badge>
                  </Flex>
                ))}
              </Flex>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* ===== Top Users This Month ===== */}
      <Card>
        <CardHeader>
          <Heading size='md' color={textColor}>📅 Top ผู้ใช้งานประจำเดือน (ทำข้อสอบมากที่สุด)</Heading>
        </CardHeader>
        <CardBody overflowX='auto'>
          <TopUsersTable users={topUsersThisMonth} textColor={textColor} subColor={subColor} />
        </CardBody>
      </Card>

      {/* ===== Popular Courses ===== */}
      <Card>
        <CardHeader>
          <Heading size='md' color={textColor}>📚 คอร์สยอดนิยม (มีผู้เรียนมากที่สุด)</Heading>
        </CardHeader>
        <CardBody overflowX='auto'>
          {popularCourses.length === 0 ? (
            <Text color={subColor} textAlign='center' py={4}>ยังไม่มีข้อมูล</Text>
          ) : (
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>ชื่อคอร์ส</Th>
                  <Th>วิชา</Th>
                  <Th>ชั้น</Th>
                  <Th isNumeric>ผู้เรียน</Th>
                </Tr>
              </Thead>
              <Tbody>
                {popularCourses.map((c, i) => {
                  const maxStudents = popularCourses[0]?.student_count || 1
                  const pct = Math.round((c.student_count / maxStudents) * 100)
                  return (
                    <Tr key={c.id}>
                      <Td><Text fontWeight='bold' color={textColor}>{i + 1}</Text></Td>
                      <Td>
                        <Text fontWeight='semibold' color={textColor} fontSize='sm' noOfLines={1}>
                          {c.name}
                        </Text>
                      </Td>
                      <Td><Badge colorScheme='purple' fontSize='xs'>{c.subject}</Badge></Td>
                      <Td><Badge colorScheme='teal' fontSize='xs'>{c.class}</Badge></Td>
                      <Td isNumeric>
                        <Badge colorScheme='blue'>{c.student_count}</Badge>
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

    </Flex>
  )
}
